import React, { Fragment, useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import FormSection from '../formSectionNew';
import localization from 'assets/localization/localization';
import DropDownInput from 'components/sales/Input/dropDownInput/dropDownInput';
import ConstantInput from 'components/sales/Input/constantInput/constantInput';
import ButtonWithIcon from 'components/sales/buttons/buttonWithIcon/buttonWithIcon';
import Regex from 'services/utils/regex/Regex';

const SoftwareInventory = ({ data, handleValue, postSalesData }) => {
  const softwareInventory = data?.software_items?.dropdown || [];
  const allSoftwareData = data?.software_items?.all_software || [];
  // Ensure allSoftware is an array
  const allSoftware = Array.isArray(allSoftwareData) ? allSoftwareData : [];

  const findDropdownItemByServiceId = useCallback(
    (serviceId) => {
      if (!Array.isArray(softwareInventory) || softwareInventory.length === 0) return null;
      return softwareInventory.find((x) => String(x?.id) === String(serviceId)) || null;
    },
    [softwareInventory]
  );

  const getDropdownDisplayName = useCallback((item) => item?.name || item?.value || '', []);
  
  const objInput = {
    service_id: '',
    quantity: '',
    sale_unit_price: '',
    item_unit_price: '',
    specification: ''
  };

  const [inputList, setInputList] = useState([]);
  const [inputValue, setInputValue] = useState([objInput]);
  const [inputListData1, setInputListData1] = useState('');
  const [inputListData2, setInputListData2] = useState('');
  const [isError, setIsError] = useState(false);
  const nextIdRef = useRef(1);
  const [selectedSoftwareNames, setSelectedSoftwareNames] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [softwareVal, setSoftwareVal] = useState('');
  const [quantityVal, setQuantityVal] = useState('');
  const debounceTimerRef = useRef(null);
  const didPrefillRef = useRef(false);

  useEffect(() => {
    const apiSoftware = Array.isArray(postSalesData?.software) ? postSalesData.software : [];

    if (didPrefillRef.current || apiSoftware.length === 0) return;

    const normalizeRow = (row) => ({
      service_id: row?.service_id ?? '',
      quantity: row?.quantity ?? '',
      sale_unit_price: row?.sale_unit_price ?? '',
      item_unit_price: row?.item_unit_price ?? '',
      specification: row?.specification ?? '',
    });

    const prefilled = apiSoftware.map(normalizeRow);
    didPrefillRef.current = true;
    setInputValue(prefilled.length > 0 ? prefilled : [objInput]);

    // Prefill first row "display" values
    const firstServiceId = prefilled?.[0]?.service_id;
    const firstDropdownItem = findDropdownItemByServiceId(firstServiceId);
    setSoftwareVal(getDropdownDisplayName(firstDropdownItem));
    setQuantityVal(prefilled?.[0]?.quantity ?? '');
    setInputListData1(getDropdownDisplayName(firstDropdownItem));
    setInputListData2(prefilled?.[0]?.quantity ?? '');

    // Prefill remaining rows list + display name mapping
    if (prefilled.length > 1) {
      setInputList(prefilled.slice(1).map((_, idx) => ({ id: idx + 1 })));
      nextIdRef.current = prefilled.length;

      const names = {};
      prefilled.forEach((r, idx) => {
        const item = findDropdownItemByServiceId(r?.service_id);
        const displayName = getDropdownDisplayName(item);
        if (displayName) names[idx] = displayName;
      });
      setSelectedSoftwareNames(names);
    }
  }, [postSalesData?.software, findDropdownItemByServiceId, getDropdownDisplayName]);

  // Calculate total amount from inputValue
  const calculateTotalAmount = useCallback((data) => {
    let amt = 0;
    if (data && data.length > 0) {
      data.forEach((value) => {
        if (value.quantity !== '' && value.sale_unit_price !== '') {
          amt += parseFloat(value.quantity) * parseFloat(value.sale_unit_price);
        }
      });
    }
    return amt;
  }, []);

  // Send data to parent - debounced to prevent excessive re-renders
  const handleFormData = useCallback((data) => {
    if (data) {
      const amt = calculateTotalAmount(data);
      setTotalAmount(amt);
      
      handleValue({
        softwareInventoryData: data,
        totalAmount: amt,
      });
    }
  }, [calculateTotalAmount, handleValue]);

  // Debounced update to parent - updates after 300ms of no changes
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Calculate and display total immediately for user feedback
    const amt = calculateTotalAmount(inputValue);
    setTotalAmount(amt);

    // Debounce parent update to prevent excessive re-renders
    debounceTimerRef.current = setTimeout(() => {
      handleFormData(inputValue);
    }, 300);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, calculateTotalAmount, handleFormData]);

  // Handle input value changes
  const handleInputValue = (val, key, index) => {
    const list = [...inputValue];
    list[index][key] = val;
    setInputValue([...list]);
  };

  // Calculate individual row total
  const calculateRowTotal = (quantity, salesPrice) => {
    if (quantity && salesPrice) {
      return (parseFloat(quantity) * parseFloat(salesPrice)).toFixed(2);
    }
    return '0.00';
  };

  // Get base price from all_software (array or object)
  const getBasePrice = (softwareId) => {
    if (!allSoftwareData) {
      return '';
    }
    
    // If it's an array
    if (Array.isArray(allSoftware) && allSoftware.length > 0) {
      const software = allSoftware.find(item => item.id === softwareId);
      return software?.base_price || '';
    }
    
    // If it's an object (key-value pairs where key is the ID)
    if (typeof allSoftwareData === 'object' && !Array.isArray(allSoftwareData)) {
      return allSoftwareData[softwareId]?.base_price || '';
    }
    
    return '';
  };

  // Handle software selection and auto-fill base price
  const handleSoftwareSelection = (val, index) => {
    const list = [...inputValue];
    list[index]['service_id'] = val.id;
    list[index]['item_unit_price'] = getBasePrice(val.id);
    
    // Update selected software names map
    setSelectedSoftwareNames(prev => ({
      ...prev,
      [index]: val.value
    }));
    
    setInputValue([...list]);
    
    // Immediate update for dropdown selection
    handleFormData([...list]);
  };

  // Add more software items
  const addSoftware = () => {
    if (
      !inputListData1 ||
      !inputListData2 ||
      isError
    ) {
      setIsError(true);
      return false;
    }
    
    const newInputValue = [
      ...inputValue,
      { service_id: '', quantity: '', sale_unit_price: '', item_unit_price: '', specification: '' },
    ];
    
    setInputValue(newInputValue);
    setInputList([...inputList, { id: nextIdRef.current }]);
    nextIdRef.current += 1;
    setIsError(false);
    setInputListData1('');
    setInputListData2('');
    
    // Immediate update when adding row
    handleFormData(newInputValue);
  };

  // Remove software item
  const removeSoftware = (index = null) => {
    setInputListData1(inputValue[index + 1]?.service_id);
    setInputListData2(inputValue[index + 1]?.quantity);
    let removeArray = inputValue.splice(index + 1, 1);
    inputList.splice(index, 1);
    const newInputValue = [...inputValue];
    const newInputList = [...inputList];
    
    setInputValue(newInputValue);
    setInputList(newInputList);
    
    // Immediate update when removing row
    handleFormData(newInputValue);
  };

  // Clear initial values
  const handleInitalValue = () => {
    setSoftwareVal('');
    setQuantityVal('');
    const resetValue = [objInput];
    setInputValue(resetValue);
    setSelectedSoftwareNames({});
    
    // Immediate update when clearing
    handleFormData(resetValue);
  };

  // Render a single row (reusable for first row and additional rows)
  const renderRow = (index, isFirstRow = false, itemId = null) => {
    const rowIndex = isFirstRow ? 0 : index + 1;
    
    return (
      <Fragment key={itemId || 'first-row'}>
        {!isFirstRow && <hr />}
        <div className="w-100 row mx-0">
          <div className="row mx-0">
            <div className="row">
              <div className="col-md-3">
                <DropDownInput
                  label={localization.softwareInventory}
                  data={softwareInventory || []}
                  getIdValue={(val) => {
                    if (isFirstRow) {
                      setSoftwareVal(val.value);
                      setInputListData1(val.value);
                    }
                    handleSoftwareSelection(val, rowIndex);
                  }}
                  value={isFirstRow ? softwareVal : selectedSoftwareNames[rowIndex] || ''}
                  required={false}
                  isStarRequired={false}
                  errorMsg={localization.softwareInventory}
                />
              </div>

              <div className="col-md-3">
                <ConstantInput
                  label={localization.quantity}
                  type="number"
                  value={isFirstRow ? quantityVal : inputValue[rowIndex]?.quantity || ''}
                  handleChange={(val) => {
                    if (isFirstRow) {
                      setQuantityVal(val);
                      setInputListData2(val);
                    }
                    handleInputValue(val, 'quantity', rowIndex);
                    setIsError(!Regex.numericNotZero.test(inputValue[rowIndex]?.quantity));
                  }}
                  required={false}
                  isStarRequired={false}
                  min={0}
                />
              </div>

              <div className="col-md-3">
                <ConstantInput
                  label="Sales Price"
                  type="number"
                  value={inputValue[rowIndex]?.sale_unit_price || ''}
                  handleChange={(val) => {
                    handleInputValue(val, 'sale_unit_price', rowIndex);
                  }}
                  min={0}
                />
              </div>

              <div className="col-md-3">
                <ConstantInput
                  label="Base Price"
                  type="number"
                  value={inputValue[rowIndex]?.item_unit_price}
                  handleChange={(val) => {
                    handleInputValue(val, 'item_unit_price', rowIndex);
                  }}
                  disabled={true}
                  min={0}
                />
              </div>

              <div className="col-md-3">
                <ConstantInput
                  label={localization.specification}
                  type="text"
                  value={inputValue[rowIndex]?.specification || ''}
                  handleChange={(val) => {
                    handleInputValue(val, 'specification', rowIndex);
                  }}
                />
              </div>

              <div className="col-md-3">
                <ConstantInput
                  label="Total Price"
                  type="number"
                  value={calculateRowTotal(inputValue[rowIndex]?.quantity, inputValue[rowIndex]?.sale_unit_price)}
                  disabled={true}
                  min={0}
                />
              </div>

              <div className="col-md-3">
                <div>
                  {isFirstRow ? (
                    inputList.length === 0 && softwareVal && quantityVal ? (
                      <Fragment>
                        <ButtonWithIcon
                          onClick={handleInitalValue}
                          title={localization.clear}
                        />
                        <br />
                      </Fragment>
                    ) : null
                  ) : (
                    <Fragment>
                      <ButtonWithIcon
                        title="Remove"
                        onClick={() => removeSoftware(index)}
                      />
                      <br />
                    </Fragment>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <FormSection title={localization.softwareInventory}>
        <div className='mb-4 d-block__align'><b>Total Amount : ₹ </b> {totalAmount.toFixed(2)}</div>
        {/* First Row */}
        {renderRow(0, true)}

        {/* Additional Rows */}
        {inputList && inputList?.length > 0 &&
          inputList.map((item, index) => renderRow(index, false, item.service_id))
        }

        <span>
          {isError ? (
            <label className="error d-block">{localization.enterField}</label>
          ) : null}
        </span>

        <div className="w-100">
          <ButtonWithIcon
            title={localization.addMoreGates}
            onClick={addSoftware}
          />
        </div>
      </FormSection>
    </Fragment>
  );
};

SoftwareInventory.propTypes = {
  data: PropTypes.any,
  handleValue: PropTypes.func,
  postSalesData: PropTypes.any,
};

export default SoftwareInventory;
